import log from 'loglevel'
import SpeechBalloonLabelLayer from '@/js/custom-layers/SpeechBalloonLabelLayer.js'
import { getLabelPixelOffset } from '@/js/util/PlaceLabelUtil.js'

/**
 * プレイスの吹き出しラベルを画面側で描くレンダラー。
 *
 * 現行はサーバー生成のMVTタイルのラベル（プレイス名のみ）を共通ライブラリが描いている。
 * 2段目に表示項目の値を出すため、地図画面ではタイル側のラベルを消して
 * このレンダラーで描き直す（`@/js/util/MapLayerLabelParams.js`）。
 *
 * 吹き出しの描画そのものは `custom-layers/SpeechBalloonLabelLayer.js` が持つ。
 * 既存のラベルの挙動（表示ズーム範囲・レイヤーメニューの表示切替・地物の絞り込み）の再現は
 * 描画データを組み立てる `UsePlaceLabel` が持つ。件数の判定も同じ絞り込みの結果を数えるため、
 * 描くものと数えるものを一致させるには1か所で決める必要がある（IFIELD_V4-7280）。
 * ここは渡されたものをそのまま描く。
 */

/**
 * レイヤーID
 * @type {String}
 */
const LAYER_ID = 'placeLabel'

/**
 * ラベルの既定のスタイル
 * マップレイヤーのスタイルが引けなかった地物に使う
 * @type {Object}
 */
const DEFAULT_STYLE = {
  fontSize: 13,
  textColor: [51, 51, 51],
  backgroundColor: [255, 255, 255, 255],
  iconColor: [51, 51, 51, 255],
  pointOffset: [0, 0],
}

/**
 * 2段目のアイコンと文字の間隔（ユーザーラベルと同じ）
 * 大きさはレイヤー側で文字サイズに追従させる
 * @type {Number}
 */
const ICON_GAP = 3

/**
 * 地物ごとのスタイルを取り出す（無ければ既定値）
 * @param {Object} d 描画データ
 * @returns {Object} スタイル
 */
const styleOf = (d) => (d && d.style) || DEFAULT_STYLE

export default class PlaceLabelRenderer {
  /**
   * コンストラクタ
   * @param {String} layerId レイヤーID
   */
  constructor({ layerId } = {}) {
    this.layerId = layerId || LAYER_ID
  }

  /**
   * レンダラー関数を作成する
   * @param {Array.<Object>} data 描画データ（地図に出すものだけ）
   *   ({ position, text, icon, style, layerKey, properties })
   * @param {Object} iconAtlas 2段目のアイコン画像
   * @param {Object} iconMapping 2段目のアイコンの位置の対応表
   * @returns {Object} レンダラー {id, render}
   */
  createRenderer(data, { iconAtlas = null, iconMapping = null } = {}) {
    const labelData = data || []
    log.debug(`[DEBUG] start render ${this.layerId}`, labelData.length)

    return {
      id: this.layerId,
      render: () => [
        new SpeechBalloonLabelLayer({
          id: this.layerId,
          data: labelData,
          getPosition: (d) => d.position,
          getText: (d) => d.text,
          getSize: (d) => styleOf(d).fontSize,
          // アトラスは1レイヤーに1枚なので、描く中で一番大きい文字サイズに合わせる
          maxFontSize:
            labelData.reduce(
              (max, d) => Math.max(max, styleOf(d).fontSize),
              0
            ) || DEFAULT_STYLE.fontSize,
          getColor: (d) => styleOf(d).textColor,
          getBackgroundColor: (d) => styleOf(d).backgroundColor,
          getIconColor: (d) => styleOf(d).iconColor,
          getIcon: (d) => d.icon || null,
          iconAtlas,
          iconMapping,
          iconGap: ICON_GAP,
          // ピンの上に出す量はマップレイヤーのマーカーの大きさで決まるため地物ごとに変わる。
          // 面・線はマーカーを持たないので持ち上げない（タイル側と同じ規則）
          basePixelOffset: (d) =>
            getLabelPixelOffset(d?.geometryType, styleOf(d).pointOffset),
          updateTriggers: {
            getText: [labelData],
            getIcon: [labelData, iconMapping],
            getSize: [labelData],
            getColor: [labelData],
            getBackgroundColor: [labelData],
          },
        }),
      ],
    }
  }
}
